import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import authService from '../../utils/authService';
import config from '../../config';

const formatToIndianCurrency = (val, withSymbol = false) => {
  if (val === undefined || val === null || val === '') return '';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '';
  const formatted = num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return withSymbol ? `₹${formatted}` : formatted;
};

const HotelFilters = ({ filters = {}, initialPriceRange, onFilterChange, onClearFilters, searchId }) => {
  const defaultMinPrice = initialPriceRange?.minPrice ?? 1000;
  const defaultMaxPrice = initialPriceRange?.maxPrice ?? 200000;

  const [localHotelName, setLocalHotelName] = useState(filters?.hotelName || '');
  const [localMinPrice, setLocalMinPrice] = useState(formatToIndianCurrency(filters?.minPrice ?? defaultMinPrice));
  const [localMaxPrice, setLocalMaxPrice] = useState(formatToIndianCurrency(filters?.maxPrice ?? defaultMaxPrice));
  const [isEditingMin, setIsEditingMin] = useState(false);
  const [isEditingMax, setIsEditingMax] = useState(false);
  const [hotelSuggestions, setHotelSuggestions] = useState([]);
  const [showHotelSuggestions, setShowHotelSuggestions] = useState(false);
  const [hotelSuggestionsLoading, setHotelSuggestionsLoading] = useState(false);
  const [filterApplying, setFilterApplying] = useState(false);
  const hotelInputRef = useRef(null);
  const hotelDebounceTimerRef = useRef(null);
  const filterDebounceTimerRef = useRef(null);
  const priceMinDebounceTimerRef = useRef(null);
  const priceMaxDebounceTimerRef = useRef(null);

  const currentMinNum = parseFloat((localMinPrice || '').toString().replace(/[^0-9.]/g, ''));
  const currentMaxNum = parseFloat((localMaxPrice || '').toString().replace(/[^0-9.]/g, ''));
  const isMinPriceCustom = (!isNaN(currentMinNum) && currentMinNum > defaultMinPrice) || (filters?.minPrice !== undefined && filters?.minPrice > defaultMinPrice);
  const isMaxPriceCustom = (!isNaN(currentMaxNum) && currentMaxNum < defaultMaxPrice) || (filters?.maxPrice !== undefined && filters?.maxPrice < defaultMaxPrice);

  const currentMin = Math.min(filters?.minPrice ?? defaultMinPrice, defaultMaxPrice);
  const currentMax = Math.max(filters?.maxPrice ?? defaultMaxPrice, defaultMinPrice);
  const minPercent = defaultMaxPrice > defaultMinPrice
    ? Math.max(0, Math.min(100, ((currentMin - defaultMinPrice) / (defaultMaxPrice - defaultMinPrice)) * 100))
    : 0;
  const maxPercent = defaultMaxPrice > defaultMinPrice
    ? Math.max(0, Math.min(100, ((currentMax - defaultMinPrice) / (defaultMaxPrice - defaultMinPrice)) * 100))
    : 100;
  const priceStep = (defaultMaxPrice - defaultMinPrice > 50000) ? 500 : (defaultMaxPrice - defaultMinPrice > 10000) ? 100 : 50;

  // Sync localHotelName when parent filters reset or change externally
  useEffect(() => {
    setLocalHotelName(filters.hotelName || '');
    if (!filters.hotelName) {
      if (hotelDebounceTimerRef.current) {
        clearTimeout(hotelDebounceTimerRef.current);
      }
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
      }
      setShowHotelSuggestions(false);
      setHotelSuggestions([]);
    }
  }, [filters.hotelName]);

  // Sync min/max price when parent filters change externally
  useEffect(() => {
    if (!isEditingMin) {
      setLocalMinPrice(formatToIndianCurrency(filters.minPrice ?? defaultMinPrice));
    }
  }, [filters.minPrice, isEditingMin, defaultMinPrice]);

  useEffect(() => {
    if (!isEditingMax) {
      setLocalMaxPrice(formatToIndianCurrency(filters.maxPrice ?? defaultMaxPrice));
    }
  }, [filters.maxPrice, isEditingMax, defaultMaxPrice]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (hotelDebounceTimerRef.current) {
        clearTimeout(hotelDebounceTimerRef.current);
      }
      if (filterDebounceTimerRef.current) {
        clearTimeout(filterDebounceTimerRef.current);
      }
      if (priceMinDebounceTimerRef.current) {
        clearTimeout(priceMinDebounceTimerRef.current);
      }
      if (priceMaxDebounceTimerRef.current) {
        clearTimeout(priceMaxDebounceTimerRef.current);
      }
    };
  }, []);

  const [suggestionsPos, setSuggestionsPos] = useState({ top: 0, left: 0, width: 250 });

  // Handle window resize and scroll to reposition dropdown
  const updateDropdownPosition = useCallback(() => {
    if (hotelInputRef.current) {
      const rect = hotelInputRef.current.getBoundingClientRect();
      const newWidth = Math.max(rect.width, 250);
      let newLeft = rect.left;
      if (newLeft + newWidth > window.innerWidth - 16) {
        newLeft = Math.max(16, window.innerWidth - newWidth - 16);
      }
      if (newLeft < 16) {
        newLeft = 16;
      }
      setSuggestionsPos({
        top: rect.bottom + 8,
        left: newLeft,
        width: newWidth
      });
    }
  }, []);

  useEffect(() => {
    if (showHotelSuggestions || hotelSuggestionsLoading) {
      updateDropdownPosition();
      
      window.addEventListener('scroll', updateDropdownPosition, { passive: true });
      window.addEventListener('resize', updateDropdownPosition, { passive: true });
      document.addEventListener('scroll', updateDropdownPosition, { passive: true, capture: true });
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
        document.removeEventListener('scroll', updateDropdownPosition, { capture: true });
      };
    }
  }, [showHotelSuggestions, hotelSuggestionsLoading, updateDropdownPosition]);

  const fetchHotelSuggestions = useCallback(async (query) => {
    if (!query.trim() || !searchId) {
      setHotelSuggestions([]);
      setShowHotelSuggestions(false);
      setHotelSuggestionsLoading(false);
      return;
    }

    try {
      setHotelSuggestionsLoading(true);
      
      const response = await fetch(
        `${config.API_BASE_URL}/api/search/hotel-suggestions?searchId=${encodeURIComponent(searchId)}&hotelName=${encodeURIComponent(query)}&page=0&size=10`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...authService.getAuthHeader()
          },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHotelSuggestions(data || []);
        setShowHotelSuggestions(data.length > 0);
      } else {
        setHotelSuggestions([]);
        setShowHotelSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching hotel suggestions:', error);
      setHotelSuggestions([]);
      setShowHotelSuggestions(false);
    } finally {
      setHotelSuggestionsLoading(false);
    }
  }, [searchId]);

  const handleHotelNameChange = (e) => {
    const value = e.target.value;
    setLocalHotelName(value); // Immediate local UI update - zero input delay
    
    // Clear existing timers
    if (hotelDebounceTimerRef.current) {
      clearTimeout(hotelDebounceTimerRef.current);
    }
    if (filterDebounceTimerRef.current) {
      clearTimeout(filterDebounceTimerRef.current);
    }
    
    // Debounce hotel suggestions (200ms)
    hotelDebounceTimerRef.current = setTimeout(() => {
      fetchHotelSuggestions(value);
    }, 200);
    
    // Debounce filter API call (400ms after user finishes typing)
    if (value.trim()) {
      filterDebounceTimerRef.current = setTimeout(() => {
        setFilterApplying(true);
        onFilterChange('hotelName', value);
        setTimeout(() => setFilterApplying(false), 200);
      }, 400);
    } else {
      setFilterApplying(true);
      onFilterChange('hotelName', '');
      setTimeout(() => setFilterApplying(false), 200);
    }
  };

  const handleHotelBlur = () => {
    setTimeout(() => {
      if (localHotelName && localHotelName.trim() && !showHotelSuggestions) {
        setFilterApplying(true);
        onFilterChange('hotelName', localHotelName);
        setTimeout(() => setFilterApplying(false), 200);
      }
      setShowHotelSuggestions(false);
    }, 150);
  };

  // Handle other filter changes with immediate effect
  const handleFilterChange = (filterName, value) => {
    console.log(`🔧 Filter change in HotelFilters: ${filterName} = ${value}`);
    setFilterApplying(true);
    onFilterChange(filterName, value);
    
    // Reset applying state after a short delay
    setTimeout(() => {
      setFilterApplying(false);
    }, 500);
  };

  // Handle debounced price changes
  const handleMinPriceChange = (e) => {
    const val = e.target.value;
    setLocalMinPrice(val);

    if (priceMinDebounceTimerRef.current) {
      clearTimeout(priceMinDebounceTimerRef.current);
    }

    const cleanVal = val.replace(/[^0-9.]/g, '');
    const num = parseFloat(cleanVal);
    if (!isNaN(num) && num >= 0) {
      priceMinDebounceTimerRef.current = setTimeout(() => {
        handleFilterChange('minPrice', num);
      }, 500);
    }
  };

  const handleMaxPriceChange = (e) => {
    const val = e.target.value;
    setLocalMaxPrice(val);

    if (priceMaxDebounceTimerRef.current) {
      clearTimeout(priceMaxDebounceTimerRef.current);
    }

    const cleanVal = val.replace(/[^0-9.]/g, '');
    const num = parseFloat(cleanVal);
    if (!isNaN(num) && num >= 0) {
      priceMaxDebounceTimerRef.current = setTimeout(() => {
        handleFilterChange('maxPrice', num);
      }, 500);
    }
  };

  // Handle dual range slider changes
  const handleMinSliderChange = (e) => {
    const rawVal = Number(e.target.value);
    const safeMax = filters?.maxPrice ?? defaultMaxPrice;
    const clampedVal = Math.min(rawVal, safeMax);
    setLocalMinPrice(formatToIndianCurrency(clampedVal));

    if (priceMinDebounceTimerRef.current) {
      clearTimeout(priceMinDebounceTimerRef.current);
    }
    priceMinDebounceTimerRef.current = setTimeout(() => {
      handleFilterChange('minPrice', clampedVal);
    }, 300);
  };

  const handleMaxSliderChange = (e) => {
    const rawVal = Number(e.target.value);
    const safeMin = filters?.minPrice ?? defaultMinPrice;
    const clampedVal = Math.max(rawVal, safeMin);
    setLocalMaxPrice(formatToIndianCurrency(clampedVal));

    if (priceMaxDebounceTimerRef.current) {
      clearTimeout(priceMaxDebounceTimerRef.current);
    }
    priceMaxDebounceTimerRef.current = setTimeout(() => {
      handleFilterChange('maxPrice', clampedVal);
    }, 300);
  };

  // Handle price input blur and formatting
  const handleMinPriceBlur = () => {
    setIsEditingMin(false);
    if (priceMinDebounceTimerRef.current) {
      clearTimeout(priceMinDebounceTimerRef.current);
    }
    const cleanVal = (localMinPrice || '').toString().replace(/[^0-9.]/g, '');
    const num = parseFloat(cleanVal);
    if (!isNaN(num) && num >= 0) {
      setLocalMinPrice(formatToIndianCurrency(num));
      if (num !== filters.minPrice) {
        handleFilterChange('minPrice', num);
      }
    } else {
      setLocalMinPrice(formatToIndianCurrency(defaultMinPrice));
      if (filters.minPrice !== defaultMinPrice) {
        handleFilterChange('minPrice', defaultMinPrice);
      }
    }
  };

  const handleMaxPriceBlur = () => {
    setIsEditingMax(false);
    if (priceMaxDebounceTimerRef.current) {
      clearTimeout(priceMaxDebounceTimerRef.current);
    }
    const cleanVal = (localMaxPrice || '').toString().replace(/[^0-9.]/g, '');
    const num = parseFloat(cleanVal);
    if (!isNaN(num) && num >= 0) {
      setLocalMaxPrice(formatToIndianCurrency(num));
      if (num !== filters.maxPrice) {
        handleFilterChange('maxPrice', num);
      }
    } else {
      setLocalMaxPrice(formatToIndianCurrency(defaultMaxPrice));
      if (filters.maxPrice !== defaultMaxPrice) {
        handleFilterChange('maxPrice', defaultMaxPrice);
      }
    }
  };

  // Handle clearing particular price filters
  const handleClearMinPrice = () => {
    if (priceMinDebounceTimerRef.current) {
      clearTimeout(priceMinDebounceTimerRef.current);
    }
    setLocalMinPrice(formatToIndianCurrency(defaultMinPrice));
    handleFilterChange('minPrice', defaultMinPrice);
  };

  const handleClearMaxPrice = () => {
    if (priceMaxDebounceTimerRef.current) {
      clearTimeout(priceMaxDebounceTimerRef.current);
    }
    setLocalMaxPrice(formatToIndianCurrency(defaultMaxPrice));
    handleFilterChange('maxPrice', defaultMaxPrice);
  };

  const handleClearPriceRange = () => {
    if (priceMinDebounceTimerRef.current) {
      clearTimeout(priceMinDebounceTimerRef.current);
    }
    if (priceMaxDebounceTimerRef.current) {
      clearTimeout(priceMaxDebounceTimerRef.current);
    }
    setLocalMinPrice(formatToIndianCurrency(defaultMinPrice));
    setLocalMaxPrice(formatToIndianCurrency(defaultMaxPrice));
    onFilterChange('priceRange', { minPrice: defaultMinPrice, maxPrice: defaultMaxPrice });
  };

  // Handle clearing hotel name filter
  const handleClearHotelName = () => {
    setLocalHotelName('');
    if (hotelDebounceTimerRef.current) {
      clearTimeout(hotelDebounceTimerRef.current);
    }
    if (filterDebounceTimerRef.current) {
      clearTimeout(filterDebounceTimerRef.current);
    }
    setShowHotelSuggestions(false);
    setHotelSuggestions([]);
    setFilterApplying(true);
    onFilterChange('hotelName', '');
    setTimeout(() => setFilterApplying(false), 200);
    if (hotelInputRef.current) {
      hotelInputRef.current.focus();
    }
  };

  const selectHotel = (hotelName) => {
    setLocalHotelName(hotelName);
    
    // Clear any existing timers first
    if (hotelDebounceTimerRef.current) {
      clearTimeout(hotelDebounceTimerRef.current);
    }
    if (filterDebounceTimerRef.current) {
      clearTimeout(filterDebounceTimerRef.current);
    }
    
    // Close suggestions immediately
    setShowHotelSuggestions(false);
    setHotelSuggestions([]);
    
    // Update the filter and trigger immediate application
    setFilterApplying(true);
    onFilterChange('hotelName', hotelName);
    
    setTimeout(() => {
      setFilterApplying(false);
    }, 200);
  };

  const handleHotelKeyDown = (e) => {
    if (!showHotelSuggestions || hotelSuggestions.length === 0) {
      // If Enter is pressed and no suggestions, apply the current filter
      if (e.key === 'Enter') {
        e.preventDefault();
        console.log('🔍 Applying hotel name filter via Enter key:', localHotelName);
        handleFilterChange('hotelName', localHotelName);
        setShowHotelSuggestions(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const firstSuggestion = document.querySelector('.hotel-suggestions div:first-child');
        if (firstSuggestion) firstSuggestion.focus();
        break;
      case 'Escape':
        setShowHotelSuggestions(false);
        break;
      case 'Enter':
        e.preventDefault();
        if (hotelSuggestions.length > 0) {
          selectHotel(hotelSuggestions[0]);
        } else {
          // Apply current filter if no suggestions
          console.log('🔍 Applying hotel name filter via Enter key:', localHotelName);
          handleFilterChange('hotelName', localHotelName);
          setShowHotelSuggestions(false);
        }
        break;
    }
  };

  return (
    <div className="space-y-6">
      {/* Filter Status */}
      <div className="flex items-center justify-between">
        {filterApplying && (
          <div className="flex items-center text-sm text-blue-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Applying filters...
          </div>
        )}
      </div>
      
      {/* Hotel Name Filter */}
      <div className="relative" style={{ zIndex: 1000 }}>
        <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Hotel Name
        </label>
        <div className="relative">
          <input
            ref={hotelInputRef}
            type="text"
            value={localHotelName}
            onChange={handleHotelNameChange}
            onKeyDown={handleHotelKeyDown}
            onBlur={handleHotelBlur}
            placeholder="Search by hotel name..."
            className="w-full px-4 py-3 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors duration-200"
          />
          {(localHotelName || filters.hotelName) ? (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                handleClearHotelName();
              }}
              onClick={handleClearHotelName}
              title="Remove hotel name filter"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-full transition-colors focus:outline-none cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          ) : (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          )}
        </div>
        
        {(showHotelSuggestions || hotelSuggestionsLoading) && createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998] bg-transparent"
              onClick={() => setShowHotelSuggestions(false)}
            />
            <div 
              className="hotel-suggestions fixed bg-white border border-gray-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto z-[9999]"
              style={{ 
                top: `${suggestionsPos.top}px`,
                left: `${suggestionsPos.left}px`,
                width: `${suggestionsPos.width}px`,
                minWidth: '250px'
              }}
            >
              {hotelSuggestionsLoading ? (
                <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  Loading suggestions...
                </div>
              ) : hotelSuggestions.length > 0 ? (
                hotelSuggestions.map((hotel, index) => (
                  <div
                    key={index}
                    tabIndex={0}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectHotel(hotel);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      selectHotel(hotel);
                    }}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm transition-colors duration-200 flex items-center"
                  >
                    <svg className="w-4 h-4 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    {hotel}
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">
                  No suggestions found
                </div>
              )}
            </div>
          </>,
          document.body
        )}
      </div>

      {/* Price Range */}
      <div>
        <style>{`
          .dual-range-input::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #2563eb;
            border: 2.5px solid #ffffff;
            box-shadow: 0 1px 4px rgba(0,0,0,0.35);
            cursor: pointer;
            pointer-events: auto;
            position: relative;
            z-index: 20;
            transition: transform 0.1s ease, box-shadow 0.1s ease;
          }
          .dual-range-input::-webkit-slider-thumb:hover {
            transform: scale(1.2);
            box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.2);
          }
          .dual-range-input::-webkit-slider-thumb:active {
            transform: scale(1.3);
            box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.3);
          }
          .dual-range-input::-moz-range-thumb {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #2563eb;
            border: 2.5px solid #ffffff;
            box-shadow: 0 1px 4px rgba(0,0,0,0.35);
            cursor: pointer;
            pointer-events: auto;
            position: relative;
            z-index: 20;
          }
          .dual-range-input::-moz-range-thumb:hover {
            transform: scale(1.2);
            box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.2);
          }
        `}</style>

        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-800 flex items-center">
            <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
            Price Range
          </label>
          {(isMinPriceCustom || isMaxPriceCustom) && (
            <button
              type="button"
              onClick={handleClearPriceRange}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center hover:underline focus:outline-none"
              title="Reset price range to default"
            >
              Reset
            </button>
          )}
        </div>

        {/* Dual Range Slider Bar */}
        <div className="px-1 pt-2 pb-3 mb-2">
          <div className="relative h-6 flex items-center">
            {/* Background Track */}
            <div className="absolute w-full h-2 bg-gray-200 rounded-full"></div>

            {/* Active Range Highlight */}
            <div
              className="absolute h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"
              style={{
                left: `${minPercent}%`,
                width: `${Math.max(0, maxPercent - minPercent)}%`
              }}
            ></div>

            {/* Min Range Slider */}
            <input
              type="range"
              min={defaultMinPrice}
              max={defaultMaxPrice}
              step={priceStep}
              value={currentMin}
              onChange={handleMinSliderChange}
              className="dual-range-input absolute w-full h-2 bg-transparent appearance-none pointer-events-none"
              style={{
                zIndex: currentMin > defaultMaxPrice - 1000 ? 5 : 3
              }}
            />

            {/* Max Range Slider */}
            <input
              type="range"
              min={defaultMinPrice}
              max={defaultMaxPrice}
              step={priceStep}
              value={currentMax}
              onChange={handleMaxSliderChange}
              className="dual-range-input absolute w-full h-2 bg-transparent appearance-none pointer-events-none"
              style={{
                zIndex: 4
              }}
            />
          </div>

          {/* Min and Max Range Labels below slider */}
          <div className="flex justify-between items-center text-[11px] text-gray-400 font-medium px-0.5 -mt-0.5">
            <span>Min: {formatToIndianCurrency(defaultMinPrice, true)}</span>
            <span>Max: {formatToIndianCurrency(defaultMaxPrice, true)}</span>
          </div>
        </div>

        {/* Minimum and Maximum Numeric Inputs */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Minimum</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xs font-semibold text-gray-500 pointer-events-none">
                ₹
              </span>
              <input
                type="text"
                value={localMinPrice}
                onFocus={() => setIsEditingMin(true)}
                onChange={handleMinPriceChange}
                onBlur={handleMinPriceBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className={`w-full pl-6 ${isMinPriceCustom ? 'pr-7' : 'pr-3'} py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors duration-200`}
                placeholder="Min"
              />
              {isMinPriceCustom && (
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleClearMinPrice();
                  }}
                  onClick={handleClearMinPrice}
                  title="Remove minimum price filter"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-full transition-colors focus:outline-none cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Maximum</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xs font-semibold text-gray-500 pointer-events-none">
                ₹
              </span>
              <input
                type="text"
                value={localMaxPrice}
                onFocus={() => setIsEditingMax(true)}
                onChange={handleMaxPriceChange}
                onBlur={handleMaxPriceBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                className={`w-full pl-6 ${isMaxPriceCustom ? 'pr-7' : 'pr-3'} py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors duration-200`}
                placeholder="Max"
              />
              {isMaxPriceCustom && (
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleClearMaxPrice();
                  }}
                  onClick={handleClearMaxPrice}
                  title="Remove maximum price filter"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded-full transition-colors focus:outline-none cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Selected Price Range Badge */}
        <div className="text-sm font-medium text-gray-700 text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg py-2.5 px-3 border border-blue-200 shadow-sm">
          {formatToIndianCurrency(filters.minPrice ?? defaultMinPrice, true)} - {formatToIndianCurrency(filters.maxPrice ?? defaultMaxPrice, true)}
        </div>
      </div>

      {/* Star Rating */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
          Star Rating
        </label>
        <div className="space-y-3">
          {[
            { key: 'twoStars', label: '2 Stars', stars: 2, color: 'text-yellow-400' },
            { key: 'threeStars', label: '3 Stars', stars: 3, color: 'text-yellow-400' },
            { key: 'fourStars', label: '4 Stars', stars: 4, color: 'text-yellow-500' },
            { key: 'fiveStars', label: '5 Stars', stars: 5, color: 'text-yellow-500' }
          ].map(({ key, label, stars, color }) => (
            <label key={key} className="flex items-center cursor-pointer group p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
              <div className="relative">
                <input 
                  type="checkbox" 
                  checked={filters[key]}
                  onChange={(e) => handleFilterChange(key, e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 border-2 rounded-md transition-all duration-200 ${
                  filters[key] 
                    ? 'bg-blue-500 border-blue-500' 
                    : 'border-gray-300 group-hover:border-blue-400'
                }`}>
                  {filters[key] && (
                    <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="ml-3 flex items-center space-x-2">
                <span className="text-sm text-gray-900 font-medium">{label}</span>
                <div className="flex">
                  {Array.from({ length: stars }, (_, i) => (
                    <svg key={i} className={`w-4 h-4 ${color}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                  {Array.from({ length: 5 - stars }, (_, i) => (
                    <svg key={i} className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Matched Hotels Filter */}
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          Multi-Vendor Rates
        </label>
        <label className="flex items-center cursor-pointer group p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200">
          <div className="relative">
            <input 
              type="checkbox" 
              checked={filters.onlyMatched || false}
              onChange={(e) => handleFilterChange('onlyMatched', e.target.checked)}
              className="sr-only"
            />
            <div className={`w-5 h-5 border-2 rounded-md transition-all duration-200 ${
              filters.onlyMatched 
                ? 'bg-blue-500 border-blue-500' 
                : 'border-gray-300 group-hover:border-blue-400'
            }`}>
              {filters.onlyMatched && (
                <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
          <div className="ml-3 flex flex-col">
            <span className="text-sm text-gray-900 font-medium flex items-center">
              Only Matched Hotels
              <span className="ml-2 px-1.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded">
                2+ Providers
              </span>
            </span>
            <span className="text-xs text-gray-500">Show hotels mapped across multiple suppliers</span>
          </div>
        </label>
      </div>

      {/* Clear Filters Button */}
      <div className="pt-4 border-t border-gray-200">
        <button
          onClick={onClearFilters}
          disabled={!onClearFilters}
          className="w-full bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Clear All Filters
        </button>
      </div>
    </div>
  );
};

export default HotelFilters;